import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import Table from "../../components/table";
import Alert from "../../components/alert";
import Badge from "../../components/badge";
import Button from "../../components/button";
import { SearchBox } from "../../components/input-functions";
import MultiSelect, {
  MultiSelectOption,
} from "../../components/searchable-multi-select";
import { RoutePaths } from "../../models";
import useScreenName from "../../hooks/useScreenName";
import { dateToDDMMYYYY, splitKeyWithSpace } from "../../utils";
import { getDisplayStatus } from "./underwritingUtils";
import useQueryParams from "../../hooks/useQueryParams";
import { buildUnderwritingUiPolicyDetailUrl } from "../underwriting-ui/underwritingUi.constants";
import {
  UnderwritingPolicyAttachment,
  UnderwritingPolicyAttachmentStatus,
  PolicyAttachmentFilterOptions,
  PolicyAttachmentFilters,
  UpdatePolicyAttachmentStatusRequest,
  PolicyAttachmentCreationOptions,
} from "../../models/underwritingPolicyAttachment";
import { Segment } from "../../models/segment";
import dispatch, { ForbiddenError } from "../../middleware";
import {
  getPolicyAttachments,
  getPolicyAttachmentFilterOptions,
  updatePolicyAttachmentStatus,
  getPolicyAttachmentCreationOptions,
  getActiveSegments,
} from "../../actions/policy-attachment.action";
import CreatePolicyAttachmentModal from "./CreatePolicyAttachmentModal";
import PolicyAttachmentDetailsModal from "./PolicyAttachmentDetailsModal";

interface PolicyAttachmentTableData {
  id: JSX.Element;
  employerId: JSX.Element;
  policies: JSX.Element;
  status: JSX.Element;
  description: string;
  createdAt: string;
  actions: JSX.Element;
}

type QueryParamsType = {
  employerIds: string;
  policies: string;
  statusList: string;
  experimentNames: string;
  attachmentId: string;
};

const DEFAULT_QUERY_PARAMS: QueryParamsType = {
  employerIds: "",
  policies: "",
  statusList: "ACTIVE",
  experimentNames: "",
  attachmentId: "",
};

const columns = [
  { field: "id", label: "ID" },
  { field: "employerId", label: "Employer ID" },
  { field: "policies", label: "Policies" },
  { field: "status", label: "Status" },
  { field: "createdAt", label: "Created At" },
  { field: "actions", label: "Actions" },
];

export default function UnderwritingPolicyAttachmentsScreen() {
  useScreenName("Credit Decision");

  const history = useHistory();
  const storeDispatch = useDispatch();
  const [queryParams, setQueryParams] =
    useQueryParams<QueryParamsType>(DEFAULT_QUERY_PARAMS);

  const [policyAttachments, setPolicyAttachments] = useState<
    UnderwritingPolicyAttachment[]
  >([]);
  const [filterOptions, setFilterOptions] =
    useState<PolicyAttachmentFilterOptions>({
      employerIds: [],
      policies: [],
      experimentNames: [],
      statuses: [],
    });
  const [creationOptions, setCreationOptions] =
    useState<PolicyAttachmentCreationOptions>({
      employers: [],
      availablePolicies: [],
    });
  const [segments, setSegments] = useState<Segment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] =
    useState<UnderwritingPolicyAttachment | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [attachmentsNotAuthorized, setAttachmentsNotAuthorized] = useState(false);
  const [filterOptionsNotAuthorized, setFilterOptionsNotAuthorized] = useState(false);
  const [creationOptionsNotAuthorized, setCreationOptionsNotAuthorized] = useState(false);
  const [segmentsNotAuthorized, setSegmentsNotAuthorized] = useState(false);

  async function fetchCreationOptions() {
    try {
      const options = await dispatch(
        storeDispatch,
        getPolicyAttachmentCreationOptions()
      );
      setCreationOptions(options);
    } catch (error) {
      if (error instanceof ForbiddenError) {
        setCreationOptionsNotAuthorized(true);
      } else {
        console.error("Error fetching creation options:", error);
      }
    }
  }

  async function fetchSegments() {
    try {
      const data = await dispatch(storeDispatch, getActiveSegments());
      setSegments(data);
    } catch (error) {
      if (error instanceof ForbiddenError) {
        setSegmentsNotAuthorized(true);
      } else {
        console.error("Error fetching segments:", error);
      }
    }
  }

  function getStatusBadge(status: UnderwritingPolicyAttachmentStatus) {
    const statusTypeMap: Record<
      UnderwritingPolicyAttachmentStatus,
      "success" | "pending" | "reject"
    > = {
      [UnderwritingPolicyAttachmentStatus.ACTIVE]: "success",
      [UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT]: "success",
      [UnderwritingPolicyAttachmentStatus.SHADOW]: "pending",
      [UnderwritingPolicyAttachmentStatus.INACTIVE]: "reject",
    };

    return (
      <Badge type={statusTypeMap[status]} capitalize>
        {getDisplayStatus(status).toLowerCase()}
      </Badge>
    );
  }

  function renderId(attachment: UnderwritingPolicyAttachment) {
    return (
      <Button
        variant="blue-text"
        size="xs"
        onClick={() => {
          setSelectedAttachment(attachment);
          setIsDetailsModalOpen(true);
        }}
      >
        {attachment._id}
      </Button>
    );
  }

  function renderEmployerId(attachment: UnderwritingPolicyAttachment) {
    return (
      <Button
        variant="blue-text"
        size="xs"
        onClick={() =>
          history.push(
            `${
              RoutePaths.UNDERWRITING_POLICY_ATTACHMENTS_TIMELINE
            }?employerId=${encodeURIComponent(attachment.employerId)}`
          )
        }
      >
        {attachment.employerId}
      </Button>
    );
  }

  function renderPolicies(attachment: UnderwritingPolicyAttachment) {
    const policies = attachment.policies || [];

    if (policies.length === 0) return <>-</>;

    return (
      <>
        {policies.map((policyName, index) => (
          <span key={policyName}>
            <Link to={buildUnderwritingUiPolicyDetailUrl(policyName)}>
              {policyName}
            </Link>
            {index < policies.length - 1 ? ", " : ""}
          </span>
        ))}
      </>
    );
  }

  function renderActions(attachment: UnderwritingPolicyAttachment) {
    const canPromote = [
      UnderwritingPolicyAttachmentStatus.SHADOW,
      UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT,
    ].includes(attachment.status);
    const canDeactivate =
      attachment.status !== UnderwritingPolicyAttachmentStatus.INACTIVE;

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "4px",
          maxWidth: "180px",
        }}
      >
        {canPromote && (
          <>
            <Button
              variant="primary"
              onClick={() =>
                handleStatusChange({
                  id: attachment._id,
                  employerId: attachment.employerId,
                  toStatus: UnderwritingPolicyAttachmentStatus.ACTIVE,
                })
              }
            >
              Activate
            </Button>
            <Button
              variant="light-blue"
              onClick={() =>
                handleStatusChange({
                  id: attachment._id,
                  employerId: attachment.employerId,
                  toStatus:
                    UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT,
                })
              }
            >
              Experiment
            </Button>
          </>
        )}
        {canDeactivate && (
          <Button
            variant="danger-fill"
            onClick={() =>
              handleStatusChange({
                id: attachment._id,
                employerId: attachment.employerId,
                toStatus: UnderwritingPolicyAttachmentStatus.INACTIVE,
              })
            }
          >
            Deactivate
          </Button>
        )}
      </div>
    );
  }

  function getTableData(
    attachments: UnderwritingPolicyAttachment[]
  ): PolicyAttachmentTableData[] {
    if (!attachments || !Array.isArray(attachments)) {
      return [];
    }

    return attachments.map((attachment) => ({
      id: renderId(attachment),
      employerId: renderEmployerId(attachment),
      policies: renderPolicies(attachment),
      status: getStatusBadge(attachment.status),
      description: attachment.description || "-",
      createdAt: dateToDDMMYYYY(attachment.createdAt),
      actions: renderActions(attachment),
    }));
  }

  function buildFiltersFromQueryParams(): PolicyAttachmentFilters {
    const filters: PolicyAttachmentFilters = {};

    if (queryParams.attachmentId) {
      filters.attachmentId = queryParams.attachmentId;
    }

    if (queryParams.employerIds) {
      filters.employerIds = queryParams.employerIds.split(",").filter(Boolean);
    }

    if (queryParams.policies) {
      filters.policies = queryParams.policies.split(",").filter(Boolean);
    }

    if (queryParams.statusList) {
      filters.statusList = queryParams.statusList
        .split(",")
        .filter(Boolean) as UnderwritingPolicyAttachmentStatus[];
    }

    if (queryParams.experimentNames) {
      filters.experimentNames = queryParams.experimentNames
        .split(",")
        .filter(Boolean);
    }

    return filters;
  }

  async function fetchPolicyAttachments() {
    try {
      setIsLoading(true);
      const filters = buildFiltersFromQueryParams();
      const response = await dispatch(
        storeDispatch,
        getPolicyAttachments(filters)
      );
      setPolicyAttachments(response);
    } catch (error) {
      if (error instanceof ForbiddenError) {
        setAttachmentsNotAuthorized(true);
      } else {
        console.error("Error fetching policy attachments:", error);
        alert("Error fetching policy attachments");
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchFilterOptions() {
    try {
      const options = await dispatch(
        storeDispatch,
        getPolicyAttachmentFilterOptions()
      );
      setFilterOptions(options);
    } catch (error) {
      if (error instanceof ForbiddenError) {
        setFilterOptionsNotAuthorized(true);
      } else {
        console.error("Error fetching filter options:", error);
      }
    }
  }

  async function handleStatusChange(ctx: {
    id: string;
    employerId: string;
    toStatus: UnderwritingPolicyAttachmentStatus;
  }) {
    const { id, employerId, toStatus } = ctx;

    try {
      const payload: UpdatePolicyAttachmentStatusRequest = {
        toStatus,
        employerId,
      };

      if (toStatus === UnderwritingPolicyAttachmentStatus.INACTIVE) {
        const remarks = prompt("Please provide remarks for deactivation:");
        if (!remarks?.trim()) return;
        payload.remarks = remarks.trim();
      }

      if (toStatus === UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT) {
        const experimentName = prompt("Please provide experiment name:");
        const priority = prompt("Please provide priority (number):");
        if (!experimentName?.trim() || !priority?.trim()) return;
        const parsedPriority = Number(priority);
        if (!Number.isFinite(parsedPriority)) {
          alert("Priority must be a valid number");
          return;
        }
        payload.experimentName = experimentName.trim();
        payload.priority = parsedPriority;
      }

      await dispatch(storeDispatch, updatePolicyAttachmentStatus(id, payload));
      fetchPolicyAttachments();
      fetchFilterOptions();
    } catch (error) {
      console.error("Error updating policy attachment status:", error);
      alert("Error updating policy attachment status");
    }
  }

  function handleSearch(searchText: string) {
    setQueryParams({
      ...queryParams,
      attachmentId: searchText,
    });
  }

  function handleStatusFilterChange(statuses: string[]) {
    setQueryParams({
      ...queryParams,
      statusList: statuses.join(","),
    });
  }

  function handleEmployerFilterChange(employers: string[]) {
    setQueryParams({
      ...queryParams,
      employerIds: employers.join(","),
    });
  }

  function handlePolicyFilterChange(policies: string[]) {
    setQueryParams({
      ...queryParams,
      policies: policies.join(","),
    });
  }

  function handleExperimentNameFilterChange(experimentNames: string[]) {
    setQueryParams({
      ...queryParams,
      experimentNames: experimentNames.join(","),
    });
  }

  const initDone = useRef(false);
  const prevParamsKey = useRef("");

  useEffect(() => {
    const paramsKey = JSON.stringify(queryParams);

    if (!initDone.current) {
      initDone.current = true;
      fetchFilterOptions();
      fetchCreationOptions();
      fetchSegments();
    }

    // Prevent duplicate fetch on StrictMode remount
    if (paramsKey === prevParamsKey.current) return;
    prevParamsKey.current = paramsKey;

    fetchPolicyAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const selectedStatuses = queryParams.statusList
    ? queryParams.statusList.split(",").filter(Boolean)
    : [];
  const selectedEmployers = queryParams.employerIds
    ? queryParams.employerIds.split(",").filter(Boolean)
    : [];
  const selectedPolicies = queryParams.policies
    ? queryParams.policies.split(",").filter(Boolean)
    : [];
  const selectedExperimentNames = queryParams.experimentNames
    ? queryParams.experimentNames.split(",").filter(Boolean)
    : [];

  const statusOptions: MultiSelectOption[] =
    filterOptions?.statuses.map((status) => ({
      value: status,
      text: getDisplayStatus(status),
    })) || [];

  const employerOptions: MultiSelectOption[] =
    filterOptions?.employerIds.map((employer) => ({
      value: employer,
      text: employer,
    })) || [];

  const policyOptions: MultiSelectOption[] =
    filterOptions?.policies.map((policy) => ({
      value: policy,
      text: policy,
    })) || [];

  const experimentOptions: MultiSelectOption[] =
    filterOptions?.experimentNames.map((name) => ({
      value: name,
      text: name,
    })) || [];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Policy Attachments</h2>
        <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
          Create
        </Button>
      </div>

      <div className="dashboard-container">
        <div className="mb-4">
          <div className="mb-3">
            <SearchBox
              placeholder="Search by Attachment ID"
              value={queryParams.attachmentId}
              onSubmit={handleSearch}
            />
          </div>
          {filterOptionsNotAuthorized ? (
            <Alert type="danger" message="Not authorized to load filters." />
          ) : (
          <div className="row">
            <div className="col-md-3 mb-3">
              <label className="form-label">Filter by Status</label>
              <MultiSelect
                options={statusOptions}
                placeholder="Select statuses"
                value={selectedStatuses}
                onChange={(values) =>
                  handleStatusFilterChange(values as string[])
                }
                search
                selectAll
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Filter by Employer</label>
              <MultiSelect
                options={employerOptions}
                placeholder="Select employers"
                value={selectedEmployers}
                onChange={(values) =>
                  handleEmployerFilterChange(values as string[])
                }
                search
                selectAll
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Filter by Policy</label>
              <MultiSelect
                options={policyOptions}
                placeholder="Select policies"
                value={selectedPolicies}
                onChange={(values) =>
                  handlePolicyFilterChange(values as string[])
                }
                search
                selectAll
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Filter by Experiment</label>
              <MultiSelect
                options={experimentOptions}
                placeholder="Select Experiments"
                value={selectedExperimentNames}
                onChange={(values) =>
                  handleExperimentNameFilterChange(values as string[])
                }
                search
                selectAll
              />
            </div>
          </div>
          )}
        </div>

        {attachmentsNotAuthorized ? (
          <Alert type="danger" message="Not authorized to view policy attachments." />
        ) : (
        <Table
          loading={isLoading}
          columns={columns}
          data={getTableData(policyAttachments)}
        />
        )}
      </div>

      <CreatePolicyAttachmentModal
        isOpen={isCreateModalOpen}
        creationOptions={creationOptions}
        segments={segments}
        creationOptionsNotAuthorized={creationOptionsNotAuthorized}
        segmentsNotAuthorized={segmentsNotAuthorized}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          fetchPolicyAttachments();
        }}
      />

      <PolicyAttachmentDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        attachment={selectedAttachment}
      />
    </div>
  );
}
